/*
 * -----------------------------------------------------------------------
 *  <copyright file="event-log/grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2021 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useGridCells, useTranslation } from 'hooks';
import { UserFields, OrganisationMemberFields, EventRecordFields } from 'queries/generated/fragments.generated';
import { EventType, UserLevel } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { ExportExcelComponentInputColumn } from 'views/components/core/export-excel';

import './event-log.scss';

interface EventLogGridInterface {
    eventRecords: EventRecordFields[];
    organisationMembers: OrganisationMemberFields[];
    allUsers: UserFields[];
    userLevel: UserLevel;
    organisationName: String;
}

interface EventLogGridItem extends EventRecordFields {
    eventTypeString: String;
    eventDataString: String;
    affectedEmail?: String;
    actionedByEmail?: String;
}

const EventLogGrid = ({
    eventRecords,
    organisationMembers,
    allUsers,
    userLevel,
    organisationName,
}: EventLogGridInterface) => {
    const { t } = useTranslation();
    const { CustomizableCell } = useGridCells();
    const [gridItems, setGridItems] = useState([] as EventLogGridItem[]);

    const getEventTypeString = (eventType: EventType) => t(`admin:${eventType}`);

    const emailTypePrefix = "Email Type = '";

    const convertEmailTypeString = (emailTypeData: string) => {
        const emailType = emailTypeData.slice(emailTypePrefix.length, -1);
        const convertedEmail = t(`admin:${emailType}`);

        return `${emailTypePrefix}${convertedEmail}'`;
    };

    const getEventDataString = (eventData: string) => {
        const regex = new RegExp(`${emailTypePrefix}([a-zA-Z]+)'`);

        return eventData.replace(regex, convertEmailTypeString);
    };

    const nullToEmpty = (value: string) => value ?? '';

    const getSystemGridItem = (eventRecord: EventRecordFields): EventLogGridItem => {
        // system event records: affected user is always system user, actioned by user is always system user
        const affectedEmail = nullToEmpty(
            allUsers.find((u: UserFields) => u.id === eventRecord.affectedUserId && u.systemAdminLevel > 0)?.email,
        );

        const actionedByEmail = nullToEmpty(
            allUsers.find((u: UserFields) => u.id === eventRecord.userId && u.systemAdminLevel > 0)?.email,
        );

        return {
            ...eventRecord,
            eventTypeString: getEventTypeString(eventRecord.type),
            eventDataString: getEventDataString(eventRecord.data),
            affectedEmail,
            actionedByEmail,
        };
    };

    const getOrganisationGridItem = (eventRecord: EventRecordFields): EventLogGridItem => {
        // org event records: affected user is always org user, actioned by user may be system or org
        const affectedEmail = nullToEmpty(
            eventRecord.affectedUserId !== Constants.ObjectId.Empty
                ? allUsers.find((u: UserFields) => u.id === eventRecord.affectedUserId && u.systemAdminLevel < 1)?.email
                : organisationMembers.find(
                      (m: OrganisationMemberFields) => m.id === eventRecord.affectedOrganisationMemberId,
                  )?.email,
        );

        const actionedByEmail = nullToEmpty(
            eventRecord.userId !== Constants.ObjectId.Empty
                ? allUsers.find((u: UserFields) => u.id === eventRecord.userId)?.email
                : '',
        );

        return {
            ...eventRecord,
            eventTypeString: getEventTypeString(eventRecord.type),
            eventDataString: getEventDataString(eventRecord.data),
            affectedEmail,
            actionedByEmail,
        };
    };

    const getGridItem = (eventRecord: EventRecordFields): EventLogGridItem => {
        if (userLevel === UserLevel.SYSTEM) return getSystemGridItem(eventRecord);

        return getOrganisationGridItem(eventRecord);
    };

    const getGridItems = (): EventLogGridItem[] =>
        eventRecords.map((eventRecord: EventRecordFields) => getGridItem(eventRecord));

    const shouldUpdateGrid = () => (userLevel === UserLevel.SYSTEM || organisationMembers) && eventRecords && allUsers;

    useEffect(() => {
        if (shouldUpdateGrid()) setGridItems(getGridItems());
    }, [eventRecords, organisationMembers, allUsers]);

    const columns = [
        {
            label: t('eventLog:timestamp'),
            field: 'timestamp',
            id: 'event-time',
            minWidth: 140,
            cells: {
                data: CustomizableCell((gridItem: EventLogGridItem) =>
                    formatDateUTC_DEPRECATED(gridItem.timestamp, true, 'DD-MMM-YYYY HH:mm:ss UTC'),
                ),
            },
            filter: 'date',
        },
        {
            label: t('eventLog:ipAddress'),
            field: 'ipAddress',
            id: 'user-ip-address',
            minWidth: 140,
        },
        {
            label: t('eventLog:user'),
            field: 'affectedEmail',
            id: 'user',
            minWidth: 200,
        },
        {
            label: t('eventLog:actionedBy'),
            field: 'actionedByEmail',
            id: 'actioned-by',
            minWidth: 200,
        },
        {
            label: t('eventLog:type'),
            field: 'eventTypeString',
            id: 'event-type',
            minWidth: 200,
        },
        {
            label: t('eventLog:data'),
            field: 'eventDataString',
            id: 'event-data',
            minWidth: 200,
        },
    ];

    const mapColumns = (inputColumns: ExportExcelComponentInputColumn[]) => [...inputColumns];

    const getFileName = () => {
        const prefix = userLevel === UserLevel.ORGANISATION ? `${organisationName}_` : '';

        return `${prefix}${t('eventLog:exportFileName')}_${formatDateUTC_DEPRECATED(
            moment(),
            true,
            'YYYY-MM-DD_HH:mm:ss_UTC',
        )}`;
    };

    return (
        <ExpandableGrid
            customKey="event-log-grid"
            id="event-log"
            groupable={false}
            showPager
            scrollable="scrollable"
            reorderable
            pageable={false}
            addScroll
            resizable
            items={gridItems}
            options={{
                allowExcelExport: true,
                allowExpandCollapseAll: false,
                useFilterChips: true,
            }}
            excelExportOptions={{
                fileName: getFileName(),
                name: getFileName(),
                mapColumns,
            }}
            columns={columns}
        />
    );
};

export default React.memo(EventLogGrid);
